import React, { useContext, useEffect, useState } from "react";
import { IVerticalBarChartDataPoint, VerticalBarChart } from "@fluentui/react-charting";
import { IGraphData } from "../../types";
import { ThemeProvider } from "@fluentui/react";
import { darkTheme } from "../../AppGlobals";
import useScreenSizeDetector from "../useScreenSizeDetector";
import { OrbConfigUtilsService } from "../../services/OrbConfigUtilsService";
import { OrbUserContext } from "../OrbUserContext";

interface GrVerticalBarChartProps {
    menuData?: any[];
    graphData: IGraphData;
}

interface ChartConfig {
    Main: {
        Title?: string;
        SubTitle?: string;
        TitlesMarginBottom: number,
        TitlesAlign: string,
        PageSizeMobile: number;
        PageSizeMediumScreen: number;
        PageSizeLargeScreen: number;
        BarWidth: number;
        RoundCorners: boolean;
        EnableGradient: boolean;
        UseSingleColor: boolean;
        AutoBarColor: boolean;
        ColorColumn: string;
        Color2Column: string;
        BarTooltip: boolean;
        ShowDescription: "left" | "right" | "top" | "bottom";
    };
    YAxis: {
        AxisTitle: string;
        QueryColumn: string;
    };
    XAxis: {
        AxisTitle: string;
        QueryColumn: string;
        WrapXAxisLables: boolean;
        XAxisInnerPadding: number,
        XAxisOuterPadding: number,
    };
}

export const GrVerticalBarChart = (props: GrVerticalBarChartProps): React.ReactElement => {
    const { isMobile, isMediumScreen, isLargeScreen } = useScreenSizeDetector();
    const height: number = 500;
    const { orbConfig } = useContext(OrbUserContext);

    const [currentPage, setCurrentPage] = useState(-1);
    const [chartData, setChartData] = useState<IVerticalBarChartDataPoint[]>([]);
    const [allData, setAllData] = useState<IVerticalBarChartDataPoint[]>([]);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [graphConfig, setGraphConfig] = useState<ChartConfig>();

    const rootStyle = { width: `95%`, height: `${height}px` };

    useEffect(() => {
        const adjustData = () => {
            if (graphConfig) {
                const pageSize: number = getPageSize(graphConfig);
                const startIdx = currentPage * pageSize;
                const endIdx = startIdx + pageSize;
                const currentItems = allData.slice(startIdx, endIdx);
                setChartData(currentItems);
            } else {
                const jsonData = props.graphData.jSonProperties;
                const chartConfig: ChartConfig = JSON.parse(jsonData);

                const menuDataMap = new Map(props.menuData?.map(item => [item.ID, item]));
                const cData: IVerticalBarChartDataPoint[] = [];

                props.graphData.data.forEach((value) => {
                    let color: string | undefined = undefined;
                    let gradient: [string, string] | undefined = undefined;

                    if (value[chartConfig.Main.ColorColumn]?.trim() !== '') {
                        color = value[chartConfig.Main.ColorColumn];
                        gradient = [color, value[chartConfig.Main.Color2Column]];
                    }

                    if (chartConfig.XAxis.QueryColumn === 'AppID') {
                        const menuItem = menuDataMap.get(value[chartConfig.XAxis.QueryColumn]);
                        if (menuItem) {
                            cData.push({
                                x: menuItem.AppName,
                                y: value[chartConfig.YAxis.QueryColumn],
                                color,
                                gradient,
                            });
                        }
                    } else {
                        cData.push({
                            x: value[chartConfig.XAxis.QueryColumn],
                            y: value[chartConfig.YAxis.QueryColumn],
                            color,
                            gradient,
                        });
                    }
                });

                const pageSize: number = getPageSize(chartConfig);
                const totPages = Math.ceil(cData.length / pageSize);

                setGraphConfig(chartConfig);
                setAllData(cData);
                setTotalPages(totPages);
                setCurrentPage(0);
            }
        };

        adjustData();
    }, [props.graphData, currentPage, totalPages]);

    useEffect(() => {
        if (graphConfig) {
            setCurrentPage(0);
            const pageSize = getPageSize(graphConfig);
            const totPages = Math.ceil(allData.length / pageSize);
            setTotalPages(totPages);
        }
    }, [isMobile, isMediumScreen, isLargeScreen, graphConfig]);

    const getPageSize = (config: ChartConfig): number => {
        if (config) {
            if (isMobile) return config.Main.PageSizeMobile;
            else if (isMediumScreen) return config.Main.PageSizeMediumScreen;
            else return config.Main.PageSizeLargeScreen;
        }
        return 50;
    };

    const getChartWidth = (): number => {
        if (isMobile) return window.innerWidth * 0.9;
        else if (isMediumScreen) return window.innerWidth * 0.8;
        else return window.innerWidth * 0.7;
    };

    const handleNext = () => {
        if (currentPage < totalPages - 1) setCurrentPage(currentPage + 1);
    };

    const handlePrevious = () => {
        if (currentPage > 0) setCurrentPage(currentPage - 1);
    };

    const shouldShowPaginationControls = totalPages > 1;

    const titleTextAlign: React.CSSProperties['textAlign'] =
        ['start', 'end', 'left', 'right', 'center', 'justify', 'match-parent'].includes(graphConfig?.Main?.TitlesAlign?.toLocaleLowerCase())
            ? graphConfig.Main.TitlesAlign as React.CSSProperties['textAlign']
            : 'left';

    const titleMarginBottom: string =
        graphConfig?.Main?.TitlesMarginBottom !== undefined
            ? `${graphConfig.Main.TitlesMarginBottom}px`
            : '20px';

    return (
        <div style={rootStyle}>
            <ThemeProvider>
                {graphConfig && (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: titleMarginBottom }}>
                            {graphConfig.Main.Title && (
                                <div style={{ flex: shouldShowPaginationControls ? 1 : 'auto' }}>
                                    <div style={{ marginBottom: '10px' }}>
                                        <div style={{ textAlign: titleTextAlign }}>
                                            {OrbConfigUtilsService.checkOrbTokens(graphConfig.Main.Title, orbConfig)}
                                        </div>
                                        <div style={{ fontSize: 'small', textAlign: titleTextAlign }}>
                                            {OrbConfigUtilsService.checkOrbTokens(graphConfig.Main.SubTitle, orbConfig)}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {shouldShowPaginationControls && (
                                <div style={{ maxWidth: '65px', textAlign: 'right' }} className="chart-nav-buttons-container">
                                    <button
                                        onClick={handlePrevious}
                                        className="chart-button previous-chart-button"
                                        style={{
                                            width: '40px',
                                            marginRight: '10px',
                                            visibility: currentPage === 0 ? 'hidden' : 'visible',
                                        }}
                                    >
                                        &#8592;
                                    </button>
                                    <button
                                        onClick={handleNext}
                                        className="chart-button next-chart-button"
                                        style={{
                                            width: '40px',
                                            visibility: currentPage >= totalPages - 1 ? 'hidden' : 'visible',
                                        }}
                                    >
                                        &#8594;
                                    </button>
                                </div>
                            )}
                        </div>

                        <VerticalBarChart
                            key={`${props.graphData.title}-${chartData.length}`} // 🔑 forces remount on data/title change
                            chartTitle={props.graphData.title}
                            data={chartData}
                            hideLegend={true}
                            height={height}
                            width={getChartWidth()}
                            maxBarWidth={1000}
                            barWidth={graphConfig.Main.BarWidth ?? 'auto'}
                            roundCorners={graphConfig.Main.RoundCorners ?? false}
                            enableGradient={graphConfig.Main.EnableGradient ?? false}
                            useSingleColor={graphConfig.Main.UseSingleColor ?? false}
                            hideTooltip={!graphConfig.Main.BarTooltip}
                            showXAxisLablesTooltip={!graphConfig.XAxis.WrapXAxisLables}
                            wrapXAxisLables={graphConfig.XAxis.WrapXAxisLables ?? true}
                            enableReflow={true}
                            xAxisTitle={OrbConfigUtilsService.checkOrbTokens(graphConfig.XAxis.AxisTitle, orbConfig)}
                            yAxisTitle={OrbConfigUtilsService.checkOrbTokens(graphConfig.YAxis.AxisTitle, orbConfig)}
                            xAxisInnerPadding={graphConfig.XAxis.XAxisInnerPadding ?? undefined}
                            xAxisOuterPadding={graphConfig.XAxis.XAxisOuterPadding ?? undefined}
                        />
                    </>
                )}
            </ThemeProvider>
        </div>
    );
};
